import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { CheckIcon, MapIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Drawer } from './ui/Drawer';
import { GeofenceDrawer, SavedGeofenceGeometry } from './GeofenceDrawer';
import EditHistoryLink from './tablemaint/EditHistoryLink';
import { ContactInfoDrawer, PendingContact } from './ContactInfoDrawer';
import Notification, { NotificationContainer } from './Notification';
import { getGoogleMapsLoaderOptions } from '../config/googleMaps';
import { PermissionAwareInput, PermissionAwareButton } from '../contexts/PermissionAccessContext';
import TypeAhead from './TypeAhead';
import { getRails, RailOption } from '../services/rails.service';
import { Contact, ContactType, listContacts, getContactTypes, createContact } from '../services/contacts.service';
import { DupeLocation, dupeCheckLocations } from '../services/locations.service';
import { DuplicateLocationsModal } from './DuplicateLocationsModal';
import { titleCase } from '../utils/titleCase';
import api from '../services/api';

/** Loose shape for an incoming location row (from the table's /geofences/list). */
export interface LocationRecord {
  id: string | number;
  [key: string]: any;
}

/** Canonical, flat form shape. Every key is accepted by the backend
 *  LOCATION_FIELD_MAPPING, so it can be sent straight to PUT/POST. */
interface FormState {
  lname: string;
  location_type: string;
  cyalias: string;
  lcontact: string;
  ladd: string;
  ladd2: string;
  lcity: string;
  lstate: string;
  lzip: string;
  lcounty: string;
  lphone: string;
  lfax: string;
  rcvhours1: string;
  rcvhours2: string;
  lunchhours1: string;
  lunchhours2: string;
  droplive: string;
  communication: string;
  apptrequired: string;
  dedicatedequipment: string;
  keyaccount: string;
  salesrep: string;
  firmscode: string;
  iana: string;
  zone: string;
  traceterminal: string;
  railbilled: string;
  max_lane_capacity: number | null;
  max_deliveries: number | null;
  dwell_time_minutes: number | null;
  samsara_loc_id: string;
  recovery_time: number | null;
  allow_bobtails: string;
  allow_preplan: string;
  allow_preplan_returns: string;
  require_pickup: string;
  require_ppe: string;
  flaginstructions: string;
  specialinstructions: string;
  additionalinfo: string;
  geofencetype: string;
  coordinates: string;
  geofenceradius: number | null;
  deleted: string;
}

const EMPTY_FORM: FormState = {
  lname: '',
  location_type: '',
  cyalias: '',
  lcontact: '',
  ladd: '',
  ladd2: '',
  lcity: '',
  lstate: '',
  lzip: '',
  lcounty: '',
  lphone: '',
  lfax: '',
  rcvhours1: '',
  rcvhours2: '',
  lunchhours1: '',
  lunchhours2: '',
  droplive: '',
  communication: '',
  apptrequired: '',
  dedicatedequipment: 'N',
  keyaccount: '',
  salesrep: '',
  firmscode: '',
  iana: '',
  zone: '',
  traceterminal: '',
  railbilled: 'N',
  max_lane_capacity: null,
  max_deliveries: null,
  dwell_time_minutes: null,
  samsara_loc_id: '',
  recovery_time: null,
  allow_bobtails: 'N',
  allow_preplan: 'Y',
  allow_preplan_returns: 'Y',
  require_pickup: 'N',
  require_ppe: 'N',
  flaginstructions: 'N',
  specialinstructions: '',
  additionalinfo: '',
  geofencetype: '',
  coordinates: '',
  geofenceradius: null,
  deleted: 'N',
};

const str = (v: any): string => (v == null ? '' : String(v));
const yn = (v: any, fallback: string): string => (v === 'Y' || v === 'N' ? v : fallback);
const num = (v: any): number | null => (v == null || v === '' ? null : Number(v));

function rowToForm(loc: LocationRecord): FormState {
  return {
    lname: str(loc.lname),
    location_type: str(loc.location_type ?? loc.locationtype),
    cyalias: str(loc.cyalias ?? loc.alias),
    lcontact: str(loc.lcontact),
    ladd: str(loc.ladd),
    ladd2: str(loc.ladd2),
    lcity: str(loc.lcity),
    lstate: str(loc.lstate),
    lzip: str(loc.lzip),
    lcounty: str(loc.lcounty),
    lphone: str(loc.lphone),
    lfax: str(loc.lfax),
    rcvhours1: str(loc.rcvhours1),
    rcvhours2: str(loc.rcvhours2),
    lunchhours1: str(loc.lunchhours1),
    lunchhours2: str(loc.lunchhours2),
    droplive: str(loc.droplive).toUpperCase(),
    communication: str(loc.communication),
    apptrequired: ['Y', 'N'].includes(str(loc.apptrequired).toUpperCase()) ? str(loc.apptrequired).toUpperCase() : '',
    dedicatedequipment: yn(loc.dedicatedequipment, 'N'),
    keyaccount: str(loc.keyaccount),
    salesrep: str(loc.salesrep),
    firmscode: str(loc.firmscode),
    iana: str(loc.iana),
    zone: str(loc.zone),
    traceterminal: str(loc.traceterminal),
    railbilled: yn(loc.railbilled, 'N'),
    max_lane_capacity: num(loc.max_lane_capacity),
    max_deliveries: num(loc.max_deliveries),
    dwell_time_minutes: num(loc.dwell_time_minutes),
    samsara_loc_id: str(loc.samsara_loc_id),
    recovery_time: num(loc.recovery_time),
    allow_bobtails: yn(loc.allow_bobtails, 'N'),
    allow_preplan: yn(loc.allow_preplan, 'Y'),
    allow_preplan_returns: yn(loc.allow_preplan_returns, 'Y'),
    require_pickup: yn(loc.require_pickup, 'N'),
    require_ppe: yn(loc.require_ppe, 'N'),
    flaginstructions: yn(loc.flaginstructions, 'N'),
    specialinstructions: str(loc.specialinstructions),
    additionalinfo: str(loc.additionalinfo),
    geofencetype: str(loc.geofencetype),
    coordinates: str(loc.coordinates),
    geofenceradius: num(loc.radius ?? loc.geofenceradius),
    deleted: yn(loc.deleted, 'N'),
  };
}

// Diff form vs original; send only changed fields (matches the Dispatchers pattern).
function buildPatch(original: FormState, current: FormState): Partial<FormState> {
  const patch: Partial<FormState> = {};
  (Object.keys(current) as (keyof FormState)[]).forEach((key) => {
    if (current[key] !== original[key]) {
      // @ts-expect-error key indexing across the union value types
      patch[key] = current[key];
    }
  });
  return patch;
}

/** Circle / Polygon / None, derived from the coordinates string (same rule the backend uses). */
function geofenceShape(coordinates: string): 'Polygon' | 'Circle' | 'None' {
  if (!coordinates) return 'None';
  if (coordinates.includes(';')) return 'Polygon';
  if (coordinates.includes(',')) return 'Circle';
  return 'None';
}

// Quarter-hour clock values 00:00 … 23:45, used to build the receive-hours menus.
const QUARTER_TIMES: string[] = [];
for (let h = 0; h < 24; h++) {
  for (const m of ['00', '15', '30', '45']) {
    QUARTER_TIMES.push(`${String(h).padStart(2, '0')}:${m}`);
  }
}
const toOpts = (vals: string[]) => vals.map((v) => ({ value: v, label: v === '' ? '— None —' : v }));
// Receive Hours 1 leads with the After/Before/FCFS qualifiers; Receive Hours 2 ends at 23:59.
const RCV_HOURS_1_OPTIONS = toOpts(['', 'After', 'Before', 'FCFS', ...QUARTER_TIMES]);
const RCV_HOURS_2_OPTIONS = toOpts(['', ...QUARTER_TIMES, '23:59']);
// Lunch Start / End are plain clock times (no After/Before/FCFS qualifiers).
const LUNCH_HOURS_OPTIONS = toOpts(['', ...QUARTER_TIMES]);
const DROPLIVE_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'DROP', label: 'Drop' },
  { value: 'LIVE', label: 'Live' },
  { value: 'BOTH', label: 'Both' },
];

const YES_NO_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'Y', label: 'Yes' },
  { value: 'N', label: 'No' },
];

// Hardcoded rate zones (legacy @ratezone_data). Displayed Title Case, stored uppercase.
const RATE_ZONE_OPTIONS = [
  '', 'BALTIMORE', 'BAYPORT', 'BBC', 'CHARLESTON', 'CHICAGO', 'DALLAS', 'FTW/ALLIANCE',
  'JACKSONVILLE', 'LOCAL-EAST', 'LOCAL-RAIL', 'MEMPHIS', 'MOBILE', 'NEW ORLEANS',
  'NORFOLK', 'PASADENA', 'SAVANNAH',
].map((z) => ({ value: z, label: z === '' ? '— None —' : titleCase(z) }));

// Format a 'YYYY-MM-DD HH:MM:SS' (or empty) timestamp for read-only display.
function formatTimestamp(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value.replace(' ', 'T'));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString('en-US', {
    month: '2-digit', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
  });
}

// ---- small field primitives (kept local; mirror the Dispatchers drawer style) ----

const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  required?: boolean;
  maxLength?: number;
  type?: string;
  readOnly?: boolean;
  hint?: string;
  invalid?: boolean;
}> = ({ label, value, onChange, disabled, required, maxLength, type = 'text', readOnly, hint, invalid }) => (
  <div>
    <label className="block text-sm font-medium text-label mb-1">
      {label}
      {required && <span className="text-red-500"> *</span>}
      {hint && <span className="ml-1 text-xs font-normal text-gray-400">{hint}</span>}
    </label>
    <PermissionAwareInput
      type={type}
      aria-label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      maxLength={maxLength}
      disabled={disabled}
      readOnly={readOnly}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:text-white ${
        invalid ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
      } ${readOnly ? 'bg-gray-100 dark:bg-gray-800 cursor-not-allowed' : 'dark:bg-gray-700'}`}
    />
  </div>
);

const SelectField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  invalid?: boolean;
}> = ({ label, value, onChange, options, disabled, required, placeholder = 'Select…', invalid }) => (
  <div>
    <label className="block text-sm font-medium text-label mb-1">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <div className={invalid ? 'rounded-lg ring-2 ring-red-400' : ''}>
      <TypeAhead
        options={options}
        value={value}
        onChange={(v) => onChange(v as string)}
        onClear={() => onChange('')}
        placeholder={placeholder}
        disabled={disabled}
        showSearch
        flagInvalidValue
      />
    </div>
  </div>
);

const NumberField: React.FC<{
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => (
  <div>
    <label className="block text-sm font-medium text-label mb-1">{label}</label>
    <PermissionAwareInput
      type="number"
      value={value == null ? '' : String(value)}
      onChange={(e) => {
        const t = e.target.value.trim();
        onChange(t === '' ? null : Number(t));
      }}
      disabled={disabled}
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
    />
  </div>
);

const TextAreaField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  invalid?: boolean;
}> = ({ label, value, onChange, disabled, invalid }) => (
  <div className="md:col-span-2 lg:col-span-3">
    <label className="block text-sm font-medium text-label mb-1">{label}</label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={2}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
        invalid ? 'border-red-400 dark:border-red-500 focus:ring-red-500' : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
      }`}
    />
  </div>
);

const YNToggle: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}> = ({ label, value, onChange, disabled }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none py-1">
    <input
      type="checkbox"
      checked={value === 'Y'}
      onChange={(e) => onChange(e.target.checked ? 'Y' : 'N')}
      disabled={disabled}
      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
    />
    <span className="text-sm font-medium text-label">{label}</span>
  </label>
);

const SectionHeading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h3 className="text-sm font-semibold text-body border-b border-gray-200 dark:border-gray-700 pb-1">
    {children}
  </h3>
);

/** What a completed save reports back. Optional so existing callers that just
 *  re-fetch (e.g. LocationsEditor's `onSaved={loadData}`) can ignore it, while
 *  embedders (the reusable LocationMapField) can capture the recno + denormalized
 *  fields to map straight back into their form. */
export interface LocationSaveResult {
  id: number;
  lname: string;
  lcity: string;
  lstate: string;
  /** Alias + street — enough for a leg cell to build the same
   *  `LocationLite` a dropdown pick would have handed it. */
  cyalias: string;
  ladd: string;
}

interface LocationEditDrawerProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  location: LocationRecord | null;
  /** Create mode only: seeds the Name field (e.g. the term typed into a
   *  location typeahead before choosing "Add New Location"). */
  initialName?: string;
  onClose: () => void;
  /** Fired after a successful create/update. Receives the saved location's recno +
   *  name/city/state (undefined only if the create response had no id). */
  onSaved: (result?: LocationSaveResult) => void;
  /** Switch this drawer to edit an existing record chosen from the dupe popup. */
  onEditExisting?: (loc: DupeLocation) => void;
}

export const LocationEditDrawer: React.FC<LocationEditDrawerProps> = ({
  isOpen,
  mode,
  location,
  initialName,
  onClose,
  onSaved,
  onEditExisting,
}) => {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [baseline, setBaseline] = useState<FormState>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [locationTypes, setLocationTypes] = useState<string[]>([]);
  const [rails, setRails] = useState<RailOption[]>([]);
  const [mapOpen, setMapOpen] = useState(false);
  // Address-based dupe check (create flow). dupeBypass = user chose "Add New".
  const [dupeModalOpen, setDupeModalOpen] = useState(false);
  const [dupeLocations, setDupeLocations] = useState<DupeLocation[]>([]);
  const [dupeBypass, setDupeBypass] = useState(false);
  // Validation feedback shown as a banner INSIDE the drawer (a top-right toast
  // would render behind the modal dialog's top layer). showErrors reveals it
  // after a blocked save; saveError holds an API failure.
  const [showErrors, setShowErrors] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [success, setSuccess] = useState<{ title: string; message: string } | null>(null);

  const isCustomerLocation = form.location_type.toUpperCase() === 'CUSTOMER LOCATION';

  // Contacts sub-table. In edit mode these are persisted (loaded from the API);
  // in create mode they're held in memory (pendingContacts) and POSTed after the
  // location is created.
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pendingContacts, setPendingContacts] = useState<PendingContact[]>([]);
  const [pendingEditIndex, setPendingEditIndex] = useState<number | null>(null);
  const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [contactMode, setContactMode] = useState<'create' | 'edit'>('create');
  const [editingContactRecno, setEditingContactRecno] = useState<number | null>(null);
  const contactTypeLabel = useMemo(() => {
    const m = new Map<number, string>();
    contactTypes.forEach((t) => m.set(t.recno, titleCase(t.type)));
    return m;
  }, [contactTypes]);

  // Live validation — drives the banner and per-field red borders (both clear as
  // fields are fixed). Returns the messages and the set of invalid field keys.
  const validation = useMemo(() => {
    const errors: string[] = [];
    const invalid = new Set<string>();
    const required: [keyof FormState, string][] = [
      ['lname', 'Name'], ['location_type', 'Location Type'], ['ladd', 'Address'],
      ['lcity', 'City'], ['lstate', 'State'], ['lzip', 'Zip'],
    ];
    for (const [key, label] of required) {
      if (!String(form[key] ?? '').trim()) { errors.push(`${label} is required.`); invalid.add(key); }
    }
    if (isCustomerLocation) {
      if (!form.rcvhours1.trim() && !form.rcvhours2.trim()) {
        errors.push('At least one Receiving Hours is required.');
        invalid.add('rcvhours1'); invalid.add('rcvhours2');
      }
      if (!form.apptrequired.trim()) { errors.push('Appointment Required must be set.'); invalid.add('apptrequired'); }
      if (!form.droplive.trim()) { errors.push('Drop / Live is required.'); invalid.add('droplive'); }
      const contactCount = mode === 'create' ? pendingContacts.length : contacts.length;
      if (contactCount < 1) { errors.push('At least one contact is required.'); invalid.add('contacts'); }
    }
    // Both lunch fields are optional, but when both are set the window must run forward.
    if (form.lunchhours1 && form.lunchhours2 && form.lunchhours2 <= form.lunchhours1) {
      errors.push('Lunch End must be after Lunch Start.');
      invalid.add('lunchhours1'); invalid.add('lunchhours2');
    }
    if (form.flaginstructions === 'Y' && !form.specialinstructions.trim()) {
      errors.push('Flag Driver Instructions can’t be on while Driver Instructions are blank.');
      invalid.add('specialinstructions');
    }
    const samsaraId = form.samsara_loc_id.trim();
    if (samsaraId && (!/^[0-9]{1,64}$/.test(samsaraId) || /^0+$/.test(samsaraId))) {
      errors.push('Samsara Location ID must be a positive numeric ID (up to 64 digits), or blank.');
      invalid.add('samsara_loc_id');
    }
    return { errors, invalid };
  }, [form, isCustomerLocation, mode, pendingContacts, contacts]);

  const bannerErrors = [...(showErrors ? validation.errors : []), ...(saveError ? [saveError] : [])];
  const fieldInvalid = (key: string) => showErrors && validation.invalid.has(key);

  const locationRecno = location?.id != null ? Number(location.id) : null;

  const loadContacts = useCallback(() => {
    if (mode !== 'edit' || locationRecno == null) return;
    listContacts('locations', locationRecno)
      .then(setContacts)
      .catch((err) => console.error('Failed to load contacts:', err));
  }, [mode, locationRecno]);

  const { isLoaded: mapsLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  // Bumped to remount (and clear) the Autocomplete input — on open and on dupe Cancel.
  const [addrKey, setAddrKey] = useState(0);
  // In create mode the rest of the form stays locked until an address is entered
  // and either confirmed unique or the user chooses to add anyway.
  const [unlocked, setUnlocked] = useState(false);

  // Everything except the address lookup is disabled while saving, and (when
  // creating) until the address step is cleared.
  const fieldsDisabled = isSaving || (mode === 'create' && !unlocked);
  // Anchor used to locate the enclosing <dialog> (the Drawer renders in the top
  // layer, above everything on document.body).
  const contentRef = useRef<HTMLDivElement>(null);
  // Top anchor; scrolled into view when validation errors appear so the banner is seen.
  const topRef = useRef<HTMLDivElement>(null);

  // Google Places appends its predictions dropdown (.pac-container) to
  // document.body, which renders *behind* the drawer's top-layer <dialog>.
  // Relocate it into the dialog so the predictions are visible/clickable.
  useEffect(() => {
    if (!isOpen) return;
    const dialog = contentRef.current?.closest('dialog');
    if (!dialog) return;
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement && node.classList.contains('pac-container')) {
            dialog.appendChild(node);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, [isOpen]);

  // When a blocked save raises validation errors, scroll the drawer to the top
  // so the error banner is visible.
  useEffect(() => {
    if (showErrors || saveError) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showErrors, saveError]);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  // Wipe the address-derived fields (used when the address step is reset).
  const clearAddressFields = useCallback(() => {
    setForm((f) => ({
      ...f,
      ladd: '', ladd2: '', lcity: '', lcounty: '', lstate: '', lzip: '',
      coordinates: '', geofencetype: '', geofenceradius: null,
    }));
  }, []);

  // Reset the create-mode address step: re-lock the form and clear the address
  // fields. (The lookup input itself is only remounted when we need to clear its
  // text programmatically — see the dupe Cancel handler.)
  const resetAddressStep = useCallback(() => {
    setUnlocked(false);
    setDupeBypass(false);
    clearAddressFields();
  }, [clearAddressFields]);

  // If the user empties the lookup box on a new record, re-lock + clear.
  const handleAddrInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (mode === 'create' && !e.target.value.trim()) resetAddressStep();
    },
    [mode, resetAddressStep]
  );

  // Seed the form whenever the drawer opens (or the target record changes).
  useEffect(() => {
    if (!isOpen) return;
    setAddrKey((k) => k + 1);
    const next =
      mode === 'edit' && location
        ? rowToForm(location)
        : { ...EMPTY_FORM, lname: initialName?.trim() ?? '' };
    setForm(next);
    setBaseline(next);
    setPendingContacts([]);
    setPendingEditIndex(null);
    setShowErrors(false);
    setSaveError(null);
    setDupeModalOpen(false);
    setDupeLocations([]);
    setDupeBypass(false);
    setUnlocked(false);
  }, [isOpen, mode, location, initialName]);

  // Load reference data for the dropdowns (location types + rail terminals).
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    api
      .get('/locations/types')
      .then((res) => {
        if (!cancelled) setLocationTypes(res.data?.data || []);
      })
      .catch((err) => console.error('Failed to load location types:', err));
    getRails()
      .then((r) => {
        if (!cancelled) setRails(r);
      })
      .catch((err) => console.error('Failed to load rails:', err));
    getContactTypes()
      .then((t) => {
        if (!cancelled) setContactTypes(t);
      })
      .catch((err) => console.error('Failed to load contact types:', err));
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Load this location's contacts when editing an existing record.
  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && locationRecno != null) loadContacts();
    else setContacts([]);
  }, [isOpen, mode, locationRecno, loadContacts]);

  const handleAddContact = useCallback(() => {
    setContactMode('create');
    setEditingContactRecno(null);
    setPendingEditIndex(null);
    setContactDrawerOpen(true);
  }, []);

  // Open a persisted contact (edit mode).
  const handleEditContact = useCallback((recno: number) => {
    setContactMode('edit');
    setEditingContactRecno(recno);
    setPendingEditIndex(null);
    setContactDrawerOpen(true);
  }, []);

  // Open an in-memory pending contact (create mode).
  const handleEditPending = useCallback((idx: number) => {
    setContactMode('edit');
    setEditingContactRecno(null);
    setPendingEditIndex(idx);
    setContactDrawerOpen(true);
  }, []);

  // Stash a contact submitted from the (parent-managed) drawer.
  const handlePendingSubmit = useCallback(
    (data: PendingContact) => {
      setPendingContacts((prev) =>
        pendingEditIndex != null ? prev.map((p, i) => (i === pendingEditIndex ? data : p)) : [...prev, data]
      );
    },
    [pendingEditIndex]
  );

  const onAutocompleteLoad = useCallback((ac: google.maps.places.Autocomplete) => {
    autocompleteRef.current = ac;
  }, []);

  // Address lookup: fill address fields + county, and conditionally seed the
  // geofence center. Per spec: if the geofence is a Polygon, leave coordinates
  // alone; if it's Circular or unset, overwrite the center with Google's point
  // (defaulting a blank type to CIRCULAR).
  const onPlaceChanged = useCallback(() => {
    const place = autocompleteRef.current?.getPlace();
    if (!place || !place.geometry || !place.address_components) return;

    let streetNumber = '';
    let route = '';
    let city = '';
    let county = '';
    let state = '';
    let zip = '';
    for (const c of place.address_components) {
      const t = c.types;
      if (t.includes('street_number')) streetNumber = c.long_name;
      else if (t.includes('route')) route = c.long_name;
      else if (t.includes('locality')) city = c.long_name;
      else if (t.includes('administrative_area_level_2')) county = c.long_name.replace(/ County$/i, '');
      else if (t.includes('administrative_area_level_1')) state = c.short_name;
      else if (t.includes('postal_code')) zip = c.long_name;
    }
    const streetAddress = streetNumber ? `${streetNumber} ${route}` : route;
    const lat = place.geometry.location?.lat();
    const lng = place.geometry.location?.lng();

    setForm((f) => {
      const next: FormState = {
        ...f,
        ladd: streetAddress || f.ladd,
        lcity: city || f.lcity,
        lcounty: county || f.lcounty,
        lstate: state || f.lstate,
        lzip: zip || f.lzip,
      };
      // A Polygon geofence is left untouched. Otherwise seed a Circular geofence
      // at Google's point (radius defaults to 0.5 mi); if the lookup returned no
      // coordinates, clear the geofence fields rather than leave stale defaults.
      const isPolygon = f.geofencetype.toUpperCase().includes('POLY');
      if (!isPolygon) {
        if (lat != null && lng != null) {
          next.coordinates = `${lat},${lng}`;
          next.geofencetype = 'CIRCULAR';
          if (f.geofenceradius == null || f.geofenceradius === 0) next.geofenceradius = 0.5;
        } else {
          next.coordinates = '';
          next.geofencetype = '';
          next.geofenceradius = null;
        }
      }
      return next;
    });

    if (mode !== 'create') return;
    // Already past the address gate — just keep the looked-up values.
    if (dupeBypass || unlocked) return;
    // Check whether this address already exists (incl. deleted) and offer to edit
    // it instead. If unique, unlock the rest of the form; if dupes, show the popup.
    if (streetAddress && city && state && zip) {
      dupeCheckLocations(streetAddress, city, state, zip)
        .then((dupes) => {
          if (dupes.length) {
            setDupeLocations(dupes);
            setDupeModalOpen(true);
          } else {
            setUnlocked(true);
          }
        })
        .catch((err) => {
          console.error('Dupe-check failed:', err);
          // Don't trap the user if the check fails — let them proceed.
          setUnlocked(true);
        });
    } else {
      // A place without a full address still clears the gate.
      setUnlocked(true);
    }
  }, [mode, dupeBypass, unlocked]);

  // Geofence map saved coords back — sync into the form *and* the baseline so
  // we don't redundantly re-write them when the location is saved.
  const handleMapSaved = useCallback(
    (saved?: SavedGeofenceGeometry) => {
      if (saved) {
        const patch = {
          coordinates: saved.coordinates,
          geofencetype: saved.geofencetype,
          geofenceradius: saved.radius,
        };
        setForm((f) => ({ ...f, ...patch }));
        setBaseline((b) => ({ ...b, ...patch }));
      }
      setMapOpen(false);
      onSaved();
    },
    [onSaved]
  );

  const handleSave = useCallback(async () => {
    if (validation.errors.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setSaveError(null);

    setIsSaving(true);
    try {
      let savedResult: LocationSaveResult | undefined;
      if (mode === 'create') {
        const res = await api.post('/locations', form);
        const newId = res.data?.data?.id;
        // Persist the contacts queued while the location didn't yet have a recno.
        if (newId != null) {
          for (const pc of pendingContacts) {
            try {
              await createContact({ tablename: 'locations', tablerecno: newId, ...pc.form, notifications: pc.notifications });
            } catch (e) {
              console.error('Failed to create queued contact:', e);
            }
          }
          savedResult = {
            id: Number(newId),
            lname: form.lname,
            lcity: form.lcity,
            lstate: form.lstate,
            cyalias: form.cyalias,
            ladd: form.ladd,
          };
        }
        setSuccess({ title: 'Created', message: `${form.lname} created.` });
      } else {
        const patch = buildPatch(baseline, form);
        if (Object.keys(patch).length === 0) {
          onClose();
          return;
        }
        await api.put(`/locations/${location!.id}`, patch);
        savedResult = {
          id: Number(location!.id),
          lname: form.lname,
          lcity: form.lcity,
          lstate: form.lstate,
          cyalias: form.cyalias,
          ladd: form.ladd,
        };
        setSuccess({ title: 'Saved', message: `${form.lname} updated.` });
      }
      onSaved(savedResult);
      onClose();
    } catch (err: any) {
      console.error('Error saving location:', err);
      setSaveError(err?.response?.data?.detail || err?.message || 'Unknown error occurred');
    } finally {
      setIsSaving(false);
    }
  }, [validation, form, baseline, mode, location, onClose, onSaved, pendingContacts]);

  const shape = geofenceShape(form.coordinates);

  // Build a geofence-shaped object from the live form for the map drawer, so the
  // user works from what they just looked up (unsaved coords included).
  const geofenceForMap = useMemo(
    () => ({
      id: location?.id ?? 0,
      lname: form.lname,
      location_type: form.location_type,
      ladd: form.ladd,
      lcity: form.lcity,
      lstate: form.lstate,
      lzip: form.lzip,
      type: shape,
      location: `${form.lcity}, ${form.lstate}`,
      coordinates: form.coordinates,
      radius: form.geofenceradius ?? undefined,
      status: 'active',
    }),
    [location, form, shape]
  );

  // Unified reduced contact rows — persisted (edit) or in-memory pending (create).
  const contactRows = useMemo(() => {
    if (mode === 'edit') {
      return contacts.map((c) => ({
        key: String(c.recno),
        type: contactTypeLabel.get(c.contacttype) || String(c.contacttype),
        name: c.contactname,
        title: c.contacttitle,
        email: c.contactemail,
        onClick: () => handleEditContact(c.recno),
      }));
    }
    return pendingContacts.map((p, i) => ({
      key: `p${i}`,
      type: p.form.contacttype != null ? contactTypeLabel.get(p.form.contacttype) || String(p.form.contacttype) : '',
      name: p.form.contactname,
      title: p.form.contacttitle,
      email: p.form.contactemail,
      onClick: () => handleEditPending(i),
    }));
  }, [mode, contacts, pendingContacts, contactTypeLabel, handleEditContact, handleEditPending]);

  // Section order differs by mode. On a NEW record the address lookup is the
  // first thing that has to happen — it drives the dupe check, and the rest of
  // the form stays disabled until it clears (see `unlocked`) — so it leads,
  // with the address fields it fills sitting right behind it. Editing an
  // existing location has no such gate, so Basic Information keeps the top
  // slot there and the layout is unchanged.
  const basicSection = (
    <div className="space-y-3">
      <SectionHeading>Basic Information</SectionHeading>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <TextField label="Name" value={form.lname} onChange={(v) => set('lname', v)} required disabled={fieldsDisabled} maxLength={255} invalid={fieldInvalid('lname')} />
        <SelectField
          label="Location Type"
          value={form.location_type}
          onChange={(v) => set('location_type', v)}
          options={locationTypes.map((t) => ({ value: t, label: titleCase(t) }))}
          disabled={fieldsDisabled}
          required
          invalid={fieldInvalid('location_type')}
        />
        <TextField label="Alias" value={form.cyalias} onChange={(v) => set('cyalias', v)} disabled={fieldsDisabled} />
        <SelectField
          label="Trace Terminal"
          value={form.traceterminal}
          onChange={(v) => set('traceterminal', v)}
          options={rails.map((r) => ({ value: r.terminal, label: r.terminal }))}
          disabled={fieldsDisabled}
        />
        <TextField label="IANA" value={form.iana} onChange={(v) => set('iana', v)} disabled={fieldsDisabled} />
        <TextField label="FIRMS Code" value={form.firmscode} onChange={(v) => set('firmscode', v)} disabled={fieldsDisabled} />
        <div className="flex items-end pb-1">
          <YNToggle label="Rail Billed" value={form.railbilled} onChange={(v) => set('railbilled', v)} disabled={fieldsDisabled} />
        </div>
      </div>
    </div>
  );

  const addressLookupSection = (
    <div>
      <label className="block text-sm font-medium text-label mb-1">
        Address Lookup
      </label>
      <p className="text-xs text-muted mb-1">
        {mode === 'create' && !unlocked
          ? 'Enter an address to begin — the rest of the form unlocks once it’s confirmed not a duplicate.'
          : 'Search Google for an address to auto-fill the address, county, and coordinates.'}
      </p>
      {mapsLoaded ? (
        <Autocomplete
          key={addrKey}
          onLoad={onAutocompleteLoad}
          onPlaceChanged={onPlaceChanged}
          options={{ componentRestrictions: { country: 'us' } }}
        >
          <input
            type="text"
            defaultValue=""
            onChange={handleAddrInput}
            placeholder="Search for an address..."
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
          />
        </Autocomplete>
      ) : (
        <input
          type="text"
          disabled
          placeholder="Loading maps..."
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
        />
      )}
    </div>
  );

  const addressSection = (
    <div className="space-y-3">
      <SectionHeading>Address</SectionHeading>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <TextField label="Address 1" value={form.ladd} onChange={(v) => set('ladd', v)} disabled={fieldsDisabled} required invalid={fieldInvalid('ladd')} />
        <TextField label="Address 2" value={form.ladd2} onChange={(v) => set('ladd2', v)} disabled={fieldsDisabled} />
        <TextField label="City" value={form.lcity} onChange={(v) => set('lcity', v)} disabled={fieldsDisabled} required invalid={fieldInvalid('lcity')} />
        <TextField label="State" value={form.lstate} onChange={(v) => set('lstate', v)} disabled={fieldsDisabled} maxLength={2} required invalid={fieldInvalid('lstate')} />
        <TextField label="Zip" value={form.lzip} onChange={(v) => set('lzip', v)} disabled={fieldsDisabled} required invalid={fieldInvalid('lzip')} />
        <TextField label="County" value={form.lcounty} onChange={(v) => set('lcounty', v)} readOnly hint="(from lookup)" />
      </div>
    </div>
  );

  const title =
    mode === 'create'
      ? 'Add Location'
      : `Edit Location — ${form.lname || ''}${location?.id != null ? ` (#${location.id})` : ''}`;

  return (
    <>
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={title}
        position="right"
        width="62rem"
        footer={
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <PermissionAwareButton
              onClick={handleSave}
              disabled={fieldsDisabled}
              className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <CheckIcon className="w-4 h-4" />
                  {mode === 'create' ? 'Create Location' : 'Save Changes'}
                </>
              )}
            </PermissionAwareButton>
          </div>
        }
      >
        <div className="space-y-6" ref={contentRef}>
          <div ref={topRef} />
          {mode === 'edit' && locationRecno != null && (
            <div className="flex justify-end">
              <EditHistoryLink endpoint={`/locations/${locationRecno}/history`} />
            </div>
          )}
          {bannerErrors.length > 0 && (
            <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
              <p className="text-sm font-semibold text-red-800 dark:text-red-300">
                Please fix the following before saving:
              </p>
              <ul className="mt-1 list-disc list-inside text-sm text-red-700 dark:text-red-300">
                {bannerErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            </div>
          )}

          {mode === 'create' ? (
            <>
              {addressLookupSection}
              {addressSection}
              {basicSection}
            </>
          ) : (
            <>
              {basicSection}
              {addressLookupSection}
              {addressSection}
            </>
          )}

          {/* Geofence */}
          <div className="space-y-3">
            <SectionHeading>Geofence</SectionHeading>
            <TextField
              label="Samsara Location ID"
              value={form.samsara_loc_id}
              onChange={(v) => set('samsara_loc_id', v)}
              disabled={fieldsDisabled}
              maxLength={64}
              invalid={fieldInvalid('samsara_loc_id')}
            />
            <p className="text-xs text-muted">
              Enter the ID from Samsara Places to use that saved place and its geofence for routes.
              Leave blank to use this location’s coordinates. Existing routes already underway keep their original stops.
            </p>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
              <div className="text-sm">
                <div className="font-medium text-label">
                  {shape === 'None' ? 'No geofence set' : `${shape} geofence`}
                </div>
                <div className="text-xs text-muted break-all">
                  {form.coordinates ? form.coordinates : '—'}
                  {shape === 'Circle' && form.geofenceradius != null && ` · ${form.geofenceradius} mi radius`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMapOpen(true)}
                disabled={mode === 'create'}
                title={
                  mode === 'create'
                    ? 'Save the location first, then refine its geofence on the map'
                    : 'Edit geofence on map'
                }
                className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/30 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <MapIcon className="w-4 h-4" />
                Edit on Map
              </button>
            </div>
            {mode === 'create' && (
              <p className="text-xs text-muted">
                A looked-up address seeds a circular geofence here; you can refine it on the map after creating the location.
              </p>
            )}
          </div>

          {/* Contacts — persisted in edit mode, queued in memory while creating. */}
          <div className="space-y-2">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-1">
              <h3 className={`text-sm font-semibold ${fieldInvalid('contacts') ? 'text-red-600 dark:text-red-400' : 'text-body'}`}>
                Contacts{isCustomerLocation && <span className="text-red-500"> *</span>}
                {fieldInvalid('contacts') && <span className="ml-2 text-xs font-normal">— at least one required</span>}
              </h3>
              <button
                type="button"
                onClick={handleAddContact}
                disabled={fieldsDisabled}
                className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <PlusIcon className="w-4 h-4" /> Add Contact
              </button>
            </div>
            {contactRows.length === 0 ? (
              <p className="text-sm text-muted">No contacts yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted text-left">
                    <th className="py-1 pr-2 font-medium">Type</th>
                    <th className="py-1 px-2 font-medium">Name</th>
                    <th className="py-1 px-2 font-medium">Title</th>
                    <th className="py-1 pl-2 font-medium">Email</th>
                  </tr>
                </thead>
                <tbody>
                  {contactRows.map((c) => (
                    <tr
                      key={c.key}
                      onClick={c.onClick}
                      className="border-t border-gray-100 dark:border-gray-700/60 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    >
                      <td className="py-1.5 pr-2 text-label whitespace-nowrap">{c.type}</td>
                      <td className="py-1.5 px-2 text-label">{c.name}</td>
                      <td className="py-1.5 px-2 text-label">{c.title}</td>
                      <td className="py-1.5 pl-2 text-blue-600 dark:text-blue-400 break-all">{c.email}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Operations */}
          <div className="space-y-3">
            <SectionHeading>Operations</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <SelectField label="Receive Hours 1" value={form.rcvhours1} onChange={(v) => set('rcvhours1', v)} options={RCV_HOURS_1_OPTIONS} disabled={fieldsDisabled} required={isCustomerLocation} invalid={fieldInvalid('rcvhours1')} />
              <SelectField label="Receive Hours 2" value={form.rcvhours2} onChange={(v) => set('rcvhours2', v)} options={RCV_HOURS_2_OPTIONS} disabled={fieldsDisabled} required={isCustomerLocation} invalid={fieldInvalid('rcvhours2')} />
              <SelectField label="Lunch Start" value={form.lunchhours1} onChange={(v) => set('lunchhours1', v)} options={LUNCH_HOURS_OPTIONS} disabled={fieldsDisabled} invalid={fieldInvalid('lunchhours1')} />
              <SelectField label="Lunch End" value={form.lunchhours2} onChange={(v) => set('lunchhours2', v)} options={LUNCH_HOURS_OPTIONS} disabled={fieldsDisabled} invalid={fieldInvalid('lunchhours2')} />
              <SelectField label="Drop / Live" value={form.droplive} onChange={(v) => set('droplive', v)} options={DROPLIVE_OPTIONS} disabled={fieldsDisabled} required={isCustomerLocation} invalid={fieldInvalid('droplive')} />
              <SelectField label="Rate Zone" value={form.zone} onChange={(v) => set('zone', v)} options={RATE_ZONE_OPTIONS} disabled={fieldsDisabled} />
              <SelectField label="Appointment Required" value={form.apptrequired} onChange={(v) => set('apptrequired', v)} options={YES_NO_OPTIONS} disabled={fieldsDisabled} required={isCustomerLocation} invalid={fieldInvalid('apptrequired')} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 pt-1">
              <YNToggle label="Dedicated Equipment" value={form.dedicatedequipment} onChange={(v) => set('dedicatedequipment', v)} disabled={fieldsDisabled} />
            </div>
          </div>

          {/* Capacity & Flags */}
          <div className="space-y-3">
            <SectionHeading>Capacity &amp; Flags</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <NumberField label="Max Lane Capacity" value={form.max_lane_capacity} onChange={(v) => set('max_lane_capacity', v)} disabled={fieldsDisabled} />
              <NumberField label="Max Deliveries" value={form.max_deliveries} onChange={(v) => set('max_deliveries', v)} disabled={fieldsDisabled} />
              <NumberField label="Dwell Time (min)" value={form.dwell_time_minutes} onChange={(v) => set('dwell_time_minutes', v)} disabled={fieldsDisabled} />
              <NumberField label="Recovery Time (min)" value={form.recovery_time} onChange={(v) => set('recovery_time', v)} disabled={fieldsDisabled} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 pt-1">
              <YNToggle label="Allow Bobtails" value={form.allow_bobtails} onChange={(v) => set('allow_bobtails', v)} disabled={fieldsDisabled} />
              <YNToggle label="Allow Preplan" value={form.allow_preplan} onChange={(v) => set('allow_preplan', v)} disabled={fieldsDisabled} />
              <YNToggle label="Allow Preplan Returns" value={form.allow_preplan_returns} onChange={(v) => set('allow_preplan_returns', v)} disabled={fieldsDisabled} />
              <YNToggle label="Require Pickup #" value={form.require_pickup} onChange={(v) => set('require_pickup', v)} disabled={fieldsDisabled} />
              <YNToggle label="Require PPE" value={form.require_ppe} onChange={(v) => set('require_ppe', v)} disabled={fieldsDisabled} />
            </div>
          </div>

          {/* Instructions */}
          <div className="space-y-3">
            <SectionHeading>Instructions</SectionHeading>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="md:col-span-2 lg:col-span-3">
                <YNToggle label="Flag Driver Instructions" value={form.flaginstructions} onChange={(v) => set('flaginstructions', v)} disabled={fieldsDisabled} />
              </div>
              <TextAreaField label="Driver Instructions" value={form.specialinstructions} onChange={(v) => set('specialinstructions', v)} disabled={fieldsDisabled} invalid={fieldInvalid('specialinstructions')} />
              <TextAreaField label="Additional Info" value={form.additionalinfo} onChange={(v) => set('additionalinfo', v)} disabled={fieldsDisabled} />
            </div>
          </div>

          {/* Status (edit only) */}
          {mode === 'edit' && (
            <div className="space-y-3">
              <SectionHeading>Status</SectionHeading>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <div className="text-xs font-medium text-muted">Created</div>
                  <div className="text-sm text-label">{formatTimestamp(location?.createdatetime)}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-muted">Last Used</div>
                  <div className="text-sm text-label">{formatTimestamp(location?.lastuseddatetime)}</div>
                </div>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                <YNToggle label="Deleted" value={form.deleted} onChange={(v) => set('deleted', v)} disabled={fieldsDisabled} />
                <p className="mt-1 ml-7 text-xs text-muted">
                  Hides this location from the list. Uncheck and save to restore it.
                </p>
              </div>
            </div>
          )}

          {/* Duplicate-address popup — inside the dialog so it overlays the drawer. */}
          <DuplicateLocationsModal
            isOpen={dupeModalOpen}
            locations={dupeLocations}
            onCancel={() => {
              // Stay in the Add drawer; clear the lookup box + address fields and
              // re-lock so the user can try a different address.
              setDupeModalOpen(false);
              resetAddressStep();
              setAddrKey((k) => k + 1); // remount to clear the lookup input text
            }}
            onAddNew={() => {
              setDupeBypass(true);
              setUnlocked(true);
              setDupeModalOpen(false);
            }}
            onEditExisting={(loc) => {
              setDupeModalOpen(false);
              onEditExisting?.(loc);
            }}
          />
        </div>
      </Drawer>

      {/* Geofence map — renders on top of the edit drawer, which keeps its state. */}
      {mode === 'edit' && (
        <GeofenceDrawer
          isOpen={mapOpen}
          onClose={() => setMapOpen(false)}
          geofence={geofenceForMap}
          onSave={handleMapSaved}
        />
      )}

      {/* Contact editor — stacks on top of the location drawer. In edit mode it
          persists immediately and refreshes the list; in create mode it hands the
          contact back to be queued until the location is saved. */}
      <ContactInfoDrawer
        isOpen={contactDrawerOpen}
        mode={contactMode}
        contactRecno={editingContactRecno}
        tablename="locations"
        tablerecno={locationRecno ?? 0}
        contactTypes={contactTypes}
        onClose={() => setContactDrawerOpen(false)}
        onSaved={mode === 'edit' ? loadContacts : undefined}
        onSubmit={mode === 'create' ? handlePendingSubmit : undefined}
        pendingInitial={mode === 'create' && pendingEditIndex != null ? pendingContacts[pendingEditIndex] : null}
      />

      {success && (
        <NotificationContainer position="top-right">
          <Notification
            type="success"
            title={success.title}
            message={success.message}
            showClose
            onClose={() => setSuccess(null)}
            autoDismiss={3000}
          />
        </NotificationContainer>
      )}
    </>
  );
};

export default LocationEditDrawer;
